// src/services/notificationService.js
//
// Phase 4 — In-app notification centre.
// ----------------------------------------
// Notifications are Firestore documents at `notifications/{id}`.
// The client can create them (e.g. when a teacher saves a session and
// wants to notify the parent), list the most recent ones for a user,
// mark them as read, and subscribe to a realtime feed via
// `onSnapshot` for the bell icon in the AppBar.
//
// External delivery (SMS, WhatsApp, push, email) is handled by a
// Cloud Function that watches this same collection — see the
// roadmap's `functions/` folder. The client does not talk to Twilio
// or WhatsApp directly.

import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit as fsLimit,
  onSnapshot,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { notifyUser as notifyUserCallable } from './callableFunctions';
import { notificationService as notificationServiceV2 } from './notificationServiceV2';

const AUTH_BACKEND = process.env.REACT_APP_AUTH_BACKEND || 'firebase';

export const NOTIFICATION_CHANNELS = {
  IN_APP: 'in_app',
  PUSH: 'push',
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
  EMAIL: 'email'
};

export const NOTIFICATION_TYPES = {
  SESSION_SAVED: 'session_saved',
  REVISION_DUE: 'revision_due',
  QUALITY_DROP: 'quality_drop',
  MILESTONE: 'milestone',
  ANNOUNCEMENT: 'announcement',
  ATTENDANCE: 'attendance'
};

export const NOTIFICATION_STATUS = {
  PENDING: 'pending',
  DELIVERED: 'delivered',
  READ: 'read',
  FAILED: 'failed'
};

const COLLECTION = 'notifications';

const notificationServiceFirebase = {
  /**
   * Create a notification document via the `notifyUser` callable Cloud
   * Function. Direct client writes to /notifications/{id} are blocked
   * by Firestore rules (`allow write: if false`) — see almada §9.2.
   */
  async createNotification({
    recipientUid,
    channel = NOTIFICATION_CHANNELS.IN_APP,
    type = NOTIFICATION_TYPES.ANNOUNCEMENT,
    title = '',
    body = '',
    payload = {},
    studentId = null,
  }) {
    if (!recipientUid) {
      throw new Error('createNotification: recipientUid is required');
    }
    const result = await notifyUserCallable({
      recipientUid,
      channel,
      type,
      title,
      body,
      payload,
      studentId,
    });
    return result?.id || null;
  },

  /**
   * Fetch the most recent notifications for a user. Used by the
   * notification centre when the bell is first opened.
   */
  async listForUser(uid, { max = 30, onlyUnread = false } = {}) {
    if (!uid) return [];
    try {
      const constraints = [
        where('recipientUid', '==', uid),
        orderBy('createdAt', 'desc'),
        fsLimit(max)
      ];
      if (onlyUnread) {
        constraints.splice(1, 0, where('read', '==', false));
      }
      const q = query(collection(db, COLLECTION), ...constraints);
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.warn('notificationService.listForUser failed:', error.message);
      return [];
    }
  },

  /**
   * Subscribe to realtime updates of a user's notifications. Returns
   * the unsubscribe function so the caller can detach in a useEffect
   * cleanup. Never throws — errors are surfaced via the callback's
   * second argument.
   */
  subscribeForUser(uid, callback, { max = 20 } = {}) {
    if (!uid || typeof callback !== 'function') return () => {};
    try {
      const q = query(
        collection(db, COLLECTION),
        where('recipientUid', '==', uid),
        orderBy('createdAt', 'desc'),
        fsLimit(max)
      );
      return onSnapshot(
        q,
        (snap) => {
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          callback(items, null);
        },
        (err) => {
          console.warn('notificationService.subscribeForUser failed:', err.message);
          callback([], err);
        }
      );
    } catch (error) {
      console.warn('notificationService.subscribeForUser setup failed:', error.message);
      return () => {};
    }
  },

  async markAsRead(notificationId) {
    if (!notificationId) return;
    try {
      await updateDoc(doc(db, COLLECTION, notificationId), {
        read: true,
        status: NOTIFICATION_STATUS.READ,
        readAt: Timestamp.now()
      });
    } catch (error) {
      console.warn('notificationService.markAsRead failed:', error.message);
    }
  },

  /**
   * Batch mark every unread notification for a user as read.
   * Used by the "mark all read" button in the notification centre.
   */
  async markAllAsRead(uid) {
    if (!uid) return 0;
    try {
      const unread = await notificationServiceFirebase.listForUser(uid, {
        onlyUnread: true,
        max: 200
      });
      if (unread.length === 0) return 0;
      const batch = writeBatch(db);
      const now = Timestamp.now();
      for (const item of unread) {
        batch.update(doc(db, COLLECTION, item.id), {
          read: true,
          status: NOTIFICATION_STATUS.READ,
          readAt: now
        });
      }
      await batch.commit();
      return unread.length;
    } catch (error) {
      console.warn('notificationService.markAllAsRead failed:', error.message);
      return 0;
    }
  }
};

// Phase 2.8 selector — switches at module load.
export const notificationService = AUTH_BACKEND === 'api' ? notificationServiceV2 : notificationServiceFirebase;

/**
 * Pure helper used by the notification bell badge. Counts how many
 * items in a list are still unread. Safe for non-array input.
 */
export const countUnread = (items) => {
  if (!Array.isArray(items)) return 0;
  return items.filter((n) => n && n.read !== true).length;
};
